import React, { useMemo } from 'react';

import { useTheme } from '@cobalt/react-theme-provider';
import Box from '@cobalt/react-box';
import Flex from '@cobalt/react-flex';
import { Text } from '@cobalt/react-typography';

import { SelectProps } from '../Select';
import SelectAllCheckbox from './SelectAllCheckbox';
import SingleListor from './SingleListor';
import MultipleListor from './MultipleListor';

import { SelectOptionType } from '../types';

export const SHOWN_OPTIONS_COUNT = 20;

export interface ListorProps extends Pick<SelectProps, 'multiple' | 'moreOptionsTip' | 'style' | 'selectAllText'> {
  selectedValue: string | string[];
  options: SelectOptionType[];
  onChangeSelect: (value: string) => void;
  onChangeSelectAll?: (selectAll: boolean) => void;
}

function Listor(props: ListorProps) {
  const { selectedValue, options, multiple, moreOptionsTip, style, onChangeSelectAll } = props;

  const theme = useTheme();

  const enabledOptions = useMemo(() => {
    return options.filter((option) => !option.disabled);
  }, [options]);

  const mergedProps = useMemo(() => {
    return {
      ...props,
      options: moreOptionsTip ? options.slice(0, SHOWN_OPTIONS_COUNT) : options,
    };
  }, [moreOptionsTip, options, props]);

  const showSelectAll = useMemo(() => {
    return onChangeSelectAll && multiple && enabledOptions.length > 0;
  }, [enabledOptions.length, multiple, onChangeSelectAll]);

  const listNode = multiple ? (
    <MultipleListor {...mergedProps} selectedValue={selectedValue as string[]} />
  ) : (
    <SingleListor {...mergedProps} selectedValue={selectedValue as string} />
  );

  return (
    <Box style={style} scrollable>
      {/* Select all checkbox */}
      {showSelectAll && <SelectAllCheckbox {...mergedProps} selectedValue={selectedValue as string[]} />}

      {/* Option list */}
      {listNode}

      {/* More options tip */}
      {moreOptionsTip && (
        <Flex alignX="center" paddingX="2" paddingBottom="4">
          <Text style={{ textAlign: 'center' }} size="small" color={theme.gray500}>
            {moreOptionsTip}
          </Text>
        </Flex>
      )}
    </Box>
  );
}

export default Listor;
